export interface MiniQuestion {
  id: number;
  text: string;
  category: 'pattern' | 'resilience';
  type: number | 'flexibility' | 'steadiness' | 'selfBelief';
  isReverse?: boolean;
}

export const MINI_QUESTIONS: MiniQuestion[] = [
  { id: 1, type: 1, category: 'pattern', text: "I notice what's wrong or could be better almost before I notice what's working." },
  { id: 2, type: 5, category: 'pattern', text: "Before I act, I like to understand a situation thoroughly on my own." },
  { id: 3, type: 8, category: 'pattern', text: "I take charge naturally and say what I think, even when it creates friction." },
  { id: 4, type: 'flexibility', category: 'resilience', text: "When a plan falls apart, I can quickly think of other ways forward." },
  { id: 5, type: 2, category: 'pattern', text: "I tend to sense what other people need and step in before they ask." },
  { id: 6, type: 7, category: 'pattern', text: "I'm drawn to new ideas and possibilities, and I like to keep my options open." },
  { id: 7, type: 4, category: 'pattern', text: "I want my work and life to feel personally meaningful, not just successful." },
  { id: 8, type: 'steadiness', category: 'resilience', text: "My mood stays fairly steady, even on difficult days." },
  { id: 9, type: 9, category: 'pattern', text: "I can see every side of a disagreement and prefer to keep the peace." },
  { id: 10, type: 3, category: 'pattern', text: "I'm energised by goals and feel restless when I'm not making visible progress." },
  { id: 11, type: 6, category: 'pattern', text: "I naturally think through what could go wrong and prepare for it." },
  { id: 12, type: 'selfBelief', category: 'resilience', text: "I trust myself to handle whatever lands on my desk." },
  { id: 13, type: 1, category: 'pattern', text: "I hold myself to high standards and feel uneasy when something is done carelessly." },
  { id: 14, type: 2, category: 'pattern', text: "I find it easier to give help than to ask for it." },
  { id: 15, type: 'flexibility', category: 'resilience', isReverse: true, text: "Under pressure, I tend to get stuck on one way of seeing the problem." },
  { id: 16, type: 3, category: 'pattern', text: "I pay close attention to how my work, and I, come across to others." },
  { id: 17, type: 4, category: 'pattern', text: "I feel things deeply and often notice what's missing." },
  { id: 18, type: 5, category: 'pattern', text: "I protect my time, energy and privacy carefully." },
  { id: 19, type: 'steadiness', category: 'resilience', isReverse: true, text: "Setbacks unsettle me for longer than I'd like." },
  { id: 20, type: 6, category: 'pattern', text: "Trust matters a lot to me, and I take time to decide who and what I can rely on." },
  { id: 21, type: 7, category: 'pattern', text: "When things get heavy, I look for the upside or move on to something more positive." },
  { id: 22, type: 8, category: 'pattern', text: "I push back hard when I feel controlled, or when someone is treated unfairly." },
  { id: 23, type: 'selfBelief', category: 'resilience', isReverse: true, text: "When something new comes up, I doubt whether I'm up to it." },
  { id: 24, type: 9, category: 'pattern', text: "I sometimes go along with other people's plans to avoid conflict, then struggle to know what I want." },
];

export interface PatternDetail {
  number: number;
  name: string;
  summary: string;
  description: string;
  atYourBest: string[];
  underPressure: string[];
  leaveOut: string;
}

export const PATTERN_RESULTS: Record<number, PatternDetail> = {
  1: {
    number: 1,
    name: "Quality",
    summary: "Driven to get things right.",
    description: "You carry a clear sense of how things should be done, and you notice quickly when they fall short. People rely on you for standards, fairness and follow-through.",
    atYourBest: [
      "high standards that lift the work around you",
      "principled, fair and consistent",
      "careful, reliable delivery"
    ],
    underPressure: [
      "criticism of yourself and others",
      "difficulty letting \"good enough\" go",
      "tension when others seem careless"
    ],
    leaveOut: "Accepting that something is good enough to move on."
  },
  2: {
    number: 2,
    name: "Service",
    summary: "Driven to be helpful and connected.",
    description: "You read what people need and respond to it, often before they ask. Relationships are where you do your best work.",
    atYourBest: [
      "warm, generous and attentive",
      "builds trust and loyalty",
      "notices who is struggling"
    ],
    underPressure: [
      "overcommitting until you're running on empty",
      "finding it hard to say no or ask for help",
      "feeling unappreciated"
    ],
    leaveOut: "Asking for what you need."
  },
  3: {
    number: 3,
    name: "Delivery",
    summary: "Driven to achieve and make progress.",
    description: "You're energised by goals and visible results. You adapt quickly to what a situation needs and you get things done.",
    atYourBest: [
      "focused, efficient and motivating",
      "sets direction and delivers",
      "reads the room and adapts"
    ],
    underPressure: [
      "busyness over reflection",
      "image over honesty about what isn't working",
      "pushing past your own limits"
    ],
    leaveOut: "Slowing down long enough to notice how you actually feel."
  },
  4: {
    number: 4,
    name: "Creativity",
    summary: "Driven to find meaning and authenticity.",
    description: "You want what you do to matter and to feel true to who you are. You bring depth, originality and real empathy.",
    atYourBest: [
      "original and expressive",
      "emotionally honest",
      "sits with people in hard moments"
    ],
    underPressure: [
      "dwelling on what's missing",
      "withdrawing when misunderstood",
      "moods steering decisions"
    ],
    leaveOut: "Acting on the ordinary next step without waiting to feel like it."
  },
  5: {
    number: 5,
    name: "Insight",
    summary: "Driven to understand.",
    description: "You like to see the whole picture before you act. You think independently and bring clarity when others are caught up in the moment.",
    atYourBest: [
      "objective and calm in a crisis",
      "deep expertise",
      "clear, considered thinking"
    ],
    underPressure: [
      "holding back information or yourself",
      "overthinking instead of acting",
      "guarding time and energy too tightly"
    ],
    leaveOut: "Acting before you feel fully ready."
  },
  6: {
    number: 6,
    name: "Vigilance",
    summary: "Driven to be secure and prepared.",
    description: "You anticipate risks and think through what could go wrong, so you and the people around you are ready. Loyalty matters to you.",
    atYourBest: [
      "loyal and responsible",
      "spots problems early",
      "builds dependable teams and plans"
    ],
    underPressure: [
      "worst-case thinking",
      "second-guessing decisions",
      "doubting other people's motives, or your own judgement"
    ],
    leaveOut: "Trusting your own judgement without needing it confirmed."
  },
  7: {
    number: 7,
    name: "Vision",
    summary: "Driven to explore possibilities.",
    description: "You see options others miss and bring energy and optimism. You keep things moving and open up new ground.",
    atYourBest: [
      "inventive and enthusiastic",
      "reframes problems quickly",
      "lifts the mood and the ambition"
    ],
    underPressure: [
      "starting more than you finish",
      "avoiding painful or boring realities",
      "scattering your focus"
    ],
    leaveOut: "Staying with something difficult until it's done."
  },
  8: {
    number: 8,
    name: "Drive",
    summary: "Driven to be strong and in control of your path.",
    description: "You take charge, decide quickly and protect the people you're responsible for. You say what others are thinking.",
    atYourBest: [
      "decisive and direct",
      "protective of your people",
      "gets things moving"
    ],
    underPressure: [
      "intensity that overwhelms others",
      "pushing too hard to win",
      "finding it hard to show vulnerability"
    ],
    leaveOut: "Letting others see where you're uncertain or affected."
  },
  9: {
    number: 9,
    name: "Harmony",
    summary: "Driven to keep the peace.",
    description: "You see every side and help people find common ground. You bring calm and steadiness, especially when things are tense.",
    atYourBest: [
      "inclusive and patient",
      "a steadying presence",
      "a natural mediator"
    ],
    underPressure: [
      "avoiding conflict until issues grow",
      "going along to get along",
      "losing track of your own priorities"
    ],
    leaveOut: "Saying what you want, clearly and early."
  }
};

export const ENERGY_CENTRES = {
  gut: {
    name: "Gut (Instinctual)",
    description: "You go first to instinct and action. You tend to sense what's right and move, and you care about autonomy and fairness. The full report shows how this plays out with colleagues who want the analysis first."
  },
  heart: {
    name: "Heart (Emotional)",
    description: "You go first to feelings and relationships. You read people quickly and care how things land. The full report shows how this plays out with colleagues who want to get straight to action."
  },
  head: {
    name: "Head (Intellectual)",
    description: "You go first to thinking it through. You want to understand and plan before committing. The full report shows how this plays out with colleagues who move on instinct."
  }
};

export const RESILIENCE_COPY = {
  flexibility: {
    name: "Flexibility",
    leftLabel: "Gets stuck",
    rightLabel: "Finds another way",
    bands: {
      lower: "Under pressure you may get locked into one way of seeing things. Widening your options is worth practising.",
      mid: "You can flex your thinking, though pressure sometimes narrows it.",
      higher: "You find other ways forward when plans change."
    }
  },
  steadiness: {
    name: "Emotional steadiness",
    leftLabel: "Easily unsettled",
    rightLabel: "Steady",
    bands: {
      lower: "Setbacks may stay with you for a while. Noticing what helps you recover is a good starting point.",
      mid: "You're steady most of the time, with some days that knock you more than others.",
      higher: "Your mood stays fairly even through difficult days."
    }
  },
  selfBelief: {
    name: "Self-belief",
    leftLabel: "Doubts",
    rightLabel: "Trusts myself",
    bands: {
      lower: "You may doubt yourself when something new comes up, even when you're capable.",
      mid: "You usually trust yourself, with some doubt in unfamiliar situations.",
      higher: "You trust yourself to handle what comes your way."
    }
  },
  footerNote: "Resilience needs all three. A weaker one tends to drag the others down. This is one of the lenses the full report reads in depth."
};

// Second question index mapping (0-indexed) for each pattern
// Pattern 1 -> Q13 (idx 12)
// Pattern 2 -> Q14 (idx 13)
// Pattern 3 -> Q16 (idx 15)
// Pattern 4 -> Q17 (idx 16)
// Pattern 5 -> Q18 (idx 17)
// Pattern 6 -> Q20 (idx 19)
// Pattern 7 -> Q21 (idx 20)
// Pattern 8 -> Q22 (idx 21)
// Pattern 9 -> Q24 (idx 23)
export const PATTERN_SECOND_QUESTION_IDX: Record<number, number> = {
  1: 12,
  2: 13,
  3: 15,
  4: 16,
  5: 17,
  6: 19,
  7: 20,
  8: 21,
  9: 23,
};

export interface MiniAssessmentScoreResult {
  patternScores: Record<number, number>;
  primaryPattern: number;
  supportingPattern: number;
  leastUsedPattern: number;
  isPrimaryTied: boolean;
  tiedPrimaryPatterns: number[];
  energyCentres: {
    gut: number;
    heart: number;
    head: number;
    primaryCentre: 'gut' | 'heart' | 'head';
    isTied: boolean;
    tiedCentres: Array<'gut' | 'heart' | 'head'>;
  };
  resilience: {
    flexibility: { score: number; band: 'lower' | 'mid-range' | 'higher' };
    steadiness: { score: number; band: 'lower' | 'mid-range' | 'higher' };
    selfBelief: { score: number; band: 'lower' | 'mid-range' | 'higher' };
  };
}

export function calculateMiniAssessmentScores(rawAnswers: number[]): MiniAssessmentScoreResult {
  // rawAnswers is 0-indexed array of length 24, values 1-5
  const getAns = (qNum: number) => rawAnswers[qNum - 1] ?? 3;

  // 1. Pattern scores (range 2-10)
  const patternScores: Record<number, number> = {
    1: getAns(1) + getAns(13),
    2: getAns(5) + getAns(14),
    3: getAns(10) + getAns(16),
    4: getAns(7) + getAns(17),
    5: getAns(2) + getAns(18),
    6: getAns(11) + getAns(20),
    7: getAns(6) + getAns(21),
    8: getAns(3) + getAns(22),
    9: getAns(9) + getAns(24),
  };

  const getSecondAnswer = (patternNum: number) => {
    const qIdx = PATTERN_SECOND_QUESTION_IDX[patternNum];
    return rawAnswers[qIdx] ?? 3;
  };

  // Find max score among all patterns
  const allPatterns = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  const maxScore = Math.max(...allPatterns.map(p => patternScores[p]));
  const topPatterns = allPatterns.filter(p => patternScores[p] === maxScore);
  const isPrimaryTied = topPatterns.length > 1;

  // Primary pattern: highest score.
  // Ties: break by higher answer on that pattern's second question. If still tied: lower pattern number.
  const sortPrimaryCandidates = (patterns: number[]) => {
    return [...patterns].sort((a, b) => {
      if (patternScores[b] !== patternScores[a]) {
        return patternScores[b] - patternScores[a];
      }
      const secondB = getSecondAnswer(b);
      const secondA = getSecondAnswer(a);
      if (secondB !== secondA) {
        return secondB - secondA;
      }
      return a - b; // lower pattern number wins
    });
  };

  const sortedAll = sortPrimaryCandidates(allPatterns);
  const primaryPattern = sortedAll[0];

  // Supporting pattern: second highest score (from remaining 8 patterns).
  // Ties: break by higher answer on that pattern's second question. If still tied: lower pattern number.
  const remainingForSupporting = allPatterns.filter(p => p !== primaryPattern);
  const sortedSupporting = sortPrimaryCandidates(remainingForSupporting);
  const supportingPattern = sortedSupporting[0];

  // Least-used pattern: lowest score among patterns that are NEITHER primary NOR supporting.
  // Ties: break by LOWER answer on that pattern's second question. If still tied: HIGHER pattern number.
  const remainingForLeastUsed = allPatterns.filter(p => p !== primaryPattern && p !== supportingPattern);
  const sortedLeastUsed = [...remainingForLeastUsed].sort((a, b) => {
    if (patternScores[a] !== patternScores[b]) {
      return patternScores[a] - patternScores[b]; // lowest score first
    }
    const secondA = getSecondAnswer(a);
    const secondB = getSecondAnswer(b);
    if (secondA !== secondB) {
      return secondA - secondB; // lower answer on second question wins
    }
    return b - a; // higher pattern number wins
  });
  const leastUsedPattern = sortedLeastUsed[0];

  // 2. Energy centres
  // Gut = 8 + 9 + 1
  // Heart = 2 + 3 + 4
  // Head = 5 + 6 + 7
  const gut = patternScores[8] + patternScores[9] + patternScores[1];
  const heart = patternScores[2] + patternScores[3] + patternScores[4];
  const head = patternScores[5] + patternScores[6] + patternScores[7];

  const centres: Array<{ key: 'gut' | 'heart' | 'head'; score: number }> = [
    { key: 'gut', score: gut },
    { key: 'heart', score: heart },
    { key: 'head', score: head }
  ];
  centres.sort((a, b) => b.score - a.score);

  const topCentre = centres[0];
  const secondCentre = centres[1];
  const isCentreTied = Math.abs(topCentre.score - secondCentre.score) <= 1;
  const tiedCentres = isCentreTied ? [topCentre.key, secondCentre.key] : [topCentre.key];

  // 3. Resilience markers
  // Flexibility: Q4 + (6 - Q15)
  // Steadiness: Q8 + (6 - Q19)
  // Self-belief: Q12 + (6 - Q23)
  const calcResilience = (q1: number, q2Reverse: number) => {
    const v1 = getAns(q1);
    const v2 = 6 - getAns(q2Reverse);
    const avg = (v1 + v2) / 2;
    const score = Math.round(((avg - 1) / 4) * 100);
    let band: 'lower' | 'mid-range' | 'higher';
    if (score < 40) band = 'lower';
    else if (score <= 70) band = 'mid-range';
    else band = 'higher';
    return { score, band };
  };

  const flexibility = calcResilience(4, 15);
  const steadiness = calcResilience(8, 19);
  const selfBelief = calcResilience(12, 23);

  return {
    patternScores,
    primaryPattern,
    supportingPattern,
    leastUsedPattern,
    isPrimaryTied,
    tiedPrimaryPatterns: isPrimaryTied ? topPatterns.slice(0, 2) : [],
    energyCentres: {
      gut,
      heart,
      head,
      primaryCentre: topCentre.key,
      isTied: isCentreTied,
      tiedCentres
    },
    resilience: {
      flexibility,
      steadiness,
      selfBelief
    }
  };
}
